import { z } from 'zod'

/*
export enum RuleOutputType {
  Tags = "Tags",
  UserIds = "UserIds",
  Coordinator = "Coordinator",
}

export enum RuleOutputCoordinatorType {
  InitialCoordinator = "InitialCoordinator",
  ClinicalCoordinator = "ClinicalCoordinator",
  FinalSituationPrevisionCoordinator = "FinalSituationPrevisionCoordinator",
  AdministrativeManager = "AdministrativeCoordinator",
}

export type AllocationRuleOutputValues =
  | {
      type: RuleOutputType.Tags | RuleOutputType.UserIds;
      output: string[];
    }
  | {
      type: RuleOutputType.Coordinator;
      output: RuleOutputCoordinatorType;
    };
*/

export const RuleTypes = {
	automation: 'AutomationRule',
	allocation: 'AllocationRule',
	priority: 'PriorityRule',
	'user-exclusion': 'UserExclusionRule',
	'events-subscriptions': 'EventsSubscriptionRule',
}

export const TaskTypes = {
	// Automation
	'initial-coordinator-allocation': 'InitialCoordinatorAllocation',
	'coordinator-allocation': 'CoordinatorAllocation',
	'final-situation-coordinator-allocation':
		'FinalSituationPrevisionCoordinatorAllocation',
	'administrative-manager-allocation': 'AdministrativeManagerAllocation',

	// Allocation
	'medical-acts-clinical-approval': 'MedicalActsClinicalApproval',
	'appointment-clinical-approval': 'AppointmentClinicalApproval',
	'surgery-clinical-approval': 'SurgeryClinicalApproval',
	'relapse-clinical-approval': 'RelapseClinicalApproval',
	'transfer-clinical-approval': 'TransferClinicalApproval',
	'final-situation-prevision': 'FinalSituationPrevision',
	'non-surgical-admission-requisition': 'NonSurgicalAdmissionRequisition',
	'triage-opinion': 'TriageOpinion',
	//CM
	'triage-opinion-followup': 'TriageOpinionFollowUp',
	'management-opinion': 'ManagementOpinion',
	'relapse-opinion': 'RelapseOpinion',
	'clinical-followup-opinion': 'ClinicalFollowUpOpinion',
	'causal-link-opinion': 'CausalLinkOpinion',
	'transfer-opinion': 'TransferOpinion',
	'process-forwarding': 'ProcessProviderForwarding',
	'cancel-request-appointment': 'CancelRequestAppointment',
	'cancel-request-requisition': 'CancelRequestRequisition',
	ticket: 'Ticket',

	// Events subscriptions
	'user-notifications': 'UserNotifications',
	'system-notifications': 'SystemNotifications',
}

export const coordinators = [
	'Initial',
	'Clinical',
	'FinalSituationPrevision',
	'Administrative',
]

export const output = ['UserIds', 'Coordinator', 'Pool']
export const eventsSubscriptionOutput = ['UserIds', 'Coordinators']

export const fields = [
	'processType',
	'branch',
	'bodyPart',
	'insuranceCompany',
	'district',
	'establishment',
	// 'orderNumber',
	'requestState',
	'requestType',
	'managerRequest',
	'applicantUser',
]

export const ClinicalProcessFields = [
	'injuredState',
	'clinicalState',
	'branch',
	'insuranceCompany',
	'isPensioner',
	'managementType',
	'processType',
	'externalId',
	'establishment',
]

export const AppointmentFields = [
	'appointmentStatus',
	'finalSituation',
	'medicalSpecialty',
	'doctor',
	'establishment',
	'transport',
	'causalLink',
]

export const RequisitionFields = [
	'requisitionStatus',
	'requisitionType',
	'claimantEstablishment',
	'executantEstablishment',
	'medicalAct',
	'transport',
]

export const OpinionFields = [
	'opinionType',
	'users',
	'opinionMechanismType',
	'causalLink',
]

export const dataChangeActions = ['INSERT', 'UPDATE', 'DELETE']
export const dataChangeEntities = [
	'ClinicalProcess',
	'Appointment',
	'Requisition',
	'ClinicalOpinion',
]

export const fieldOptionsPath = {
	branch: 'branches',
	previsionType: 'previsionTypes',
	processType: 'processTypes',
	bodyPart: 'bodyParts',
	establishment: 'providerEntities',
	insuranceCompany: 'insuranceCompanies',
	district: 'districts',
	requestState: 'requisitionStates',
	requestType: 'requisitionTypes',
	UserIds: 'users',
	applicantUser: 'users',
	Coordinator: 'coordinatorOptions',
	Coordinators: 'coordinatorsOptions',
	claimantEstablishment: 'providerEntities',
	executantEstablishment: 'providerEntities',
}

export const listOperators = ['in', 'notIn']
// export const stringOperators = ['equals', 'notEquals']
export const stringOperators = ['in', 'notIn']
export const numericOperators = ['gt', 'gte', 'lt', 'lte']
export const operators = [
	...listOperators,
	...numericOperators,
	...stringOperators,
]

export const isNumeric = (field) => field === 'orderNumber'
export const isSingleOption = (field) => field === 'Coordinator'
export const isAutomationRule = (ruleType) => ruleType === RuleTypes.automation

export const getFieldOptions = (field, options) => {
	return (
		options[
			fieldOptionsPath[field] ||
				fieldOptionsPath[field?.toLowerCase()] ||
				field?.toLowerCase()
		] ?? []
	)
}

// TODO: improve getLogicalConditionScheme to use inline field options
const getLogicalConditionScheme = (options, i18n) =>
	z
		.object({
			option: z.string({ invalid_type_error: i18n`Field is required` }),
			optionGroup: z
				.string({ invalid_type_error: i18n`Entity is required` })
				.nullish()
				.optional(),
			operator: z
				.string({
					invalid_type_error: i18n`Relational operator is required`,
				})
				.optional(),
			value: z.union([
				z
					.string({ invalid_type_error: i18n`Value is required` })
					.optional(),
				z
					.array(z.string(), {
						invalid_type_error: i18n`Value is required`,
					})
					.optional(),
			]),
		})
		.strict()
		.refine(
			(data) => {
				return (
					data.optionGroup ||
					(data.option &&
						[...fields, 'previsionType'].includes(data.option))
				)
			},
			{
				message: i18n`Field is required`,
				path: ['option'],
			}
		)
		// TODO: check string operators
		.refine(
			(data) => {
				const fieldOptions = getFieldOptions(data.option, options)
				const hasFieldOptions = Number(fieldOptions?.length) > 0

				return data.option && isNumeric(data.option)
					? numericOperators.includes(data.operator)
					: hasFieldOptions
					? listOperators.includes(data.operator)
					: stringOperators.includes(data.operator)
			},
			{
				message: i18n`Relational operator is required`,
				path: ['operator'],
			}
		)
		.refine(
			(data) => {
				if (data.option === 'orderNumber') {
					return !isNaN(data.value)
				}
				const fieldOptions = getFieldOptions(data.option, options)
				const hasFieldOptions = Number(fieldOptions?.length) > 0
				if (!hasFieldOptions) {
					return (
						typeof data.value === 'string' &&
						data.value?.trim().length > 0
					)
				}
				return (
					Array.isArray(data.value) &&
					data.value.length &&
					data.value.every((value) =>
						fieldOptions.find((option) => option.value === value)
					)
				)
			},
			{
				message: i18n`Invalid value for selected field`,
				path: ['value'],
			}
		)

const priorityRuleOutput = z
	.object({
		priority: z.number().min(1).max(5),
	})
	.strict()

const automationRuleOutput = (i18n) => {
	return z
		.object({
			automate: z.boolean().default(false),
			metadata: z.string().array(),
		})
		.refine(
			(data) => {
				return data.metadata.length
			},
			{
				message: i18n`Invalid value for selected output`,
				path: ['metadata'],
			}
		)
}

const ruleOutput = (options, i18n) => {
	return z
		.union([
			z.object({
				type: z.enum(['UserIds', 'Coordinator']),
				output: z
					.union([z.string().array(), z.string()])
					.transform((value) =>
						Array.isArray(value) ? value : [value]
					),
			}),
			z.object({
				type: z.literal('Pool'),
			}),
		])
		.refine(
			(data) => {
				if (data.type === 'Pool') {
					return true
				}

				const fieldOptions = getFieldOptions(data.type, options)
				return (
					Array.isArray(data.output) &&
					data.output.length &&
					data.output.every((value) =>
						fieldOptions.find((option) => option.value === value)
					)
				)
			},
			{
				message: i18n`Invalid value for selected output`,
				path: ['output'],
			}
		)
}

const eventsSubscriptionRuleOutput = (options, i18n) => {
	return z
		.object({
			type: z.enum(eventsSubscriptionOutput),
			output: z.array(z.string()),
			message: z
				.string({
					required_error: i18n`Output message is required`,
				})
				.nonempty(),
		})
		.refine(
			(data) => {
				const fieldOptions = getFieldOptions(data.type, options)
				return (
					Array.isArray(data.output) &&
					data.output.length &&
					data.output.every((value) =>
						fieldOptions.find((option) => option.value === value)
					)
				)
			},
			{
				message: i18n`Invalid value for selected output`,
				path: ['output'],
			}
		)
}

const defaultRuleSchema = (options, i18n) => {
	return z.object({
		id: z.string().optional(),
		name: z
			.string()
			.min(1, i18n`Name must be a string with a minimum of 1 character`)
			.optional(),
		taskType: z.enum(Object.values(TaskTypes)).optional(),
		order: z.number(),
		enabled: z.boolean().optional(),
		logicalConditions: z
			.array(
				z.object({
					logicalCondition: z.array(
						getLogicalConditionScheme(options, i18n)
					),
				})
			)
			.optional(),
	})
}

const ruleSchema = (options, i18n) => {
	return z.intersection(
		defaultRuleSchema(options, i18n),
		z.object({
			output: z.union([
				ruleOutput(options, i18n),
				priorityRuleOutput,
				automationRuleOutput(i18n),
			]),
		})
	)
}

const systemEventsSubscriptionRuleSchema = (options, i18n) => {
	const eventsSubscriptionSpecifics = z.intersection(
		z.object({
			action: z.string({
				required_error: i18n`Rule action is required`,
			}),
			actionEntity: z.string({
				required_error: i18n`Rule entity is required`,
			}),
		}),
		z.object({
			output: eventsSubscriptionRuleOutput(options, i18n),
		})
	)
	return z.intersection(
		defaultRuleSchema(options, i18n),
		eventsSubscriptionSpecifics
	)
}

const userEventsSubscriptionRuleSchema = (options, i18n) => {
	const eventsSubscriptionSpecifics = z.intersection(
		z.object({
			action: z.string({
				required_error: i18n`Rule action is required`,
			}),
			actionEntity: z.string({
				required_error: i18n`Rule entity is required`,
			}),
		}),
		z.object({
			output: z.object({
				message: z
					.string({
						required_error: i18n`Output message is required`,
					})
					.nonempty(),
			}),
		})
	)
	return z.intersection(
		defaultRuleSchema(options, i18n),
		eventsSubscriptionSpecifics
	)
}

export const getRulesSchema = (options, i18n) => {
	const getUserRuleSchema = z.intersection(
		defaultRuleSchema(options, i18n),
		z.object({ ruleType: z.literal('UserExclusionRule') })
	)
	const getRuleSchema = z.intersection(
		ruleSchema(options, i18n),
		z.object({
			ruleType: z.enum([
				'AutomationRule',
				'AllocationRule',
				'PriorityRule',
			]),
		})
	)

	return z
		.object({
			rules: z.union([
				z.array(getRuleSchema),
				z.array(getUserRuleSchema),
			]),
		})
		.strict()
}

export const getEventsSubscriptionRulesSchema = (options, i18n) => {
	const getEventsSubscription = z.union([
		z.intersection(
			z.object({
				taskType: z.literal('SystemNotifications'),
			}),
			systemEventsSubscriptionRuleSchema(options, i18n)
		),
		z.intersection(
			z.object({
				taskType: z.literal('UserNotifications'),
			}),
			userEventsSubscriptionRuleSchema(options, i18n)
		),
	])

	return z
		.object({
			rules: z.array(getEventsSubscription),
		})
		.strict()
}
